import {useState, useMemo, useEffect, useCallback} from 'react';
import {BrowserRouter, Routes, Route, Navigate, useLocation} from 'react-router';
import {useLogto} from '@logto/react';
import {ThemeProvider} from '@mui/material/styles';
import {Box, Container, CssBaseline} from '@mui/material';

import RequireAuth from './services/RequireAuth';
import getTheme from './services/ThemeProvider.jsx'
import { TokenProvider } from './contexts/TokenContext';
import Callback from './pages/Callback';
import Login from './pages/Landing';
import Impressum from './pages/Impressum';
import Datenschutz from './pages/Datenschutz';
import Footer from "./components/Footer.jsx";
import AppBar from "./components/AppBar.jsx";
import TeacherDashboard from "./pages/TeacherDashboard/index.jsx";
import TeacherCourseDashboard from "./pages/TeacherCourseDashboard/index.jsx";
import TeacherTopicDashboard from "./pages/TeacherTopicDashboard/index.jsx";
import StudentDashboard from "./pages/StudentDashboard/index.jsx";
import StudentCoursePage from "./pages/StudentCoursePage/index.jsx";
import StudentTopicPage from "./pages/StudentTopicPage/index.jsx";
import {StudentGamification} from './pages/StudentDashboard/StudentGamification';
import MinigamePage from "./pages/MinigamePage/index.jsx";
import Analytics from './pages/TeacherDashboard/Analytics';
import AdminDashboard from './pages/AdminDashboard/index.jsx';
import AuditAnalytics from './pages/AdminDashboard/AuditAnalytics.jsx';
import UserManagement from './pages/AdminDashboard/UserManagement/UserManagement.jsx';
import CourseManagement from './pages/AdminDashboard/CourseManagement/CourseManagement.jsx';

export const App = () => {
    /* Section: Theme Configuration */
    const [darkMode, setDarkMode] = useState(false);
    const theme = useMemo(() => getTheme(darkMode), [darkMode]);

    /* Section: Current Logto User Info */
    const {isAuthenticated, getIdTokenClaims, getAccessToken, isLoading} = useLogto();
    const [accessToken, setAccessToken] = useState('');
    const [user, setUser] = useState(null);
    const [tokenExpiration, setTokenExpiration] = useState(null);

    /**
     * Decodes a JWT token and extracts the expiration time
     * @param {string} token - The JWT token to decode
     * @returns {number|null} - The expiration timestamp in milliseconds, or null if invalid
     */
    const decodeJwtExpiration = (token) => {
        try {
            if (!token) return null;

            // JWT tokens have 3 parts separated by dots
            const parts = token.split('.');
            if (parts.length !== 3) return null;

            // Decode the payload (second part)
            const payload = parts[1];
            // Add padding if needed for base64 decoding
            const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
            const decodedPayload = atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/'));
            const payloadObj = JSON.parse(decodedPayload);

            // Extract expiration time (exp is in seconds, convert to milliseconds)
            if (payloadObj.exp) {
                const expTimeMs = payloadObj.exp * 1000;
                const now = Date.now();
                const timeUntilExpiry = expTimeMs - now;
                console.log('Token expiry info:', {
                    expires: new Date(expTimeMs),
                    now: new Date(now),
                    timeUntilExpiryMinutes: Math.round(timeUntilExpiry / 60000),
                    isExpired: timeUntilExpiry <= 0
                });
                return expTimeMs;
            }

            return null;
        } catch (error) {
            console.error('Failed to decode JWT token:', error);
            return null;
        }
    };

    /**
     * Refreshes the access token and updates expiration time
     */
    const refreshAccessToken = useCallback(async () => {
        try {
            if (isAuthenticated) {
                console.log('Refreshing access token...');

                // Clear the current token first to avoid using expired token during refresh
                setAccessToken('');
                setTokenExpiration(null);

                // Force a fresh token by calling getAccessToken
                const newToken = await getAccessToken(import.meta.env.VITE_LOGTO_RESOURCES);
                if (newToken) {
                    const expTime = decodeJwtExpiration(newToken);

                    // Check if we actually got a new token
                    if (expTime && expTime > Date.now()) {
                        setAccessToken(newToken);
                        // Set expiration time plus 1 second (1000 ms) to refresh after expiry
                        setTokenExpiration(expTime + 1000);
                        console.log('Token refreshed successfully. Expires at:', new Date(expTime));
                        console.log('Next refresh scheduled for:', new Date(expTime + 1000));
                    } else {
                        console.error('Received an expired or invalid token');
                        // Try again in 30 seconds
                        setTimeout(refreshAccessToken, 30000);
                    }
                } else {
                    console.error('Failed to get new token');
                    // Try again in 30 seconds
                    setTimeout(refreshAccessToken, 30000);
                }
            }
        } catch (error) {
            console.error('Failed to refresh access token:', error);
            // Try again in 30 seconds
            setTimeout(refreshAccessToken, 30000);
        }
    }, [isAuthenticated, getAccessToken]);

    useEffect(() => {
        let isMounted = true;

        const fetchUserClaims = async () => {
            if (isAuthenticated) {
                try {
                    console.log('Fetching user claims');
                    const claims = await getIdTokenClaims();
                    if (isMounted) {
                        console.log('User claims fetched successfully', claims);
                        setUser(claims);
                    }
                } catch (error) {
                    console.error('Failed to fetch user claims:', error);
                }
            } else {
                // Clear user data when not authenticated
                if (isMounted) {
                    console.log('User not authenticated, clearing user data');
                    setUser(null);
                }
            }
        };

        fetchUserClaims();

        // Cleanup to prevent memory leaks
        return () => {
            isMounted = false;
        };
    }, [isAuthenticated, getIdTokenClaims]);

    useEffect(() => {
        (async () => {
            if (isAuthenticated) {
                const token = await getAccessToken(import.meta.env.VITE_LOGTO_RESOURCES);
                if (token) {
                    setAccessToken(token);
                    const expTime = decodeJwtExpiration(token);
                    if (expTime) {
                        // Set expiration time plus 1 second (1000 ms) to refresh after expiry
                        setTokenExpiration(expTime + 1000);
                        //console.log('Initial token set. Expires at:', new Date(expTime));
                        //console.log('Next refresh scheduled for:', new Date(expTime + 1000));
                    }
                }
            } else {
                // Clear token data when not authenticated
                setAccessToken('');
                setTokenExpiration(null);
            }
        })();
    }, [isAuthenticated, getAccessToken]);

    // Periodic token refresh check
    useEffect(() => {
        if (!isAuthenticated || !tokenExpiration) {
            return;
        }

        const checkTokenExpiration = () => {
            const now = Date.now();
            if (now >= tokenExpiration) {
                console.log('Token has expired, refreshing...');
                refreshAccessToken();
            }
        };

        // Check immediately in case the token is already expired
        checkTokenExpiration();

        // Set up interval to check every 5 seconds (5,000 ms) for faster response
        const intervalId = setInterval(checkTokenExpiration, 5000);

        return () => {
            clearInterval(intervalId);
        };
    }, [isAuthenticated, tokenExpiration, refreshAccessToken]);

    // Handle root redirects directly
    const RootRedirect = ({claims}) => {
        const userRoles = claims?.roles || [];
        const isStudent = userRoles.includes("student");
        const isTeacher = userRoles.includes("teacher");
        const isAdmin = userRoles.includes("admin") || userRoles.includes("administrator");
        //console.log("User roles:", userRoles); // Added for debugging

        if (isLoading) {
            return <div>Loading...</div>;
        }

        if (isAuthenticated) {
            // Redirect based on user roles (admin takes priority)
            if (isAdmin) {
                return <Navigate to="/admin/dashboard" replace/>;
            }
            if (isTeacher) {
                return <Navigate to="/teacher/dashboard" replace/>;
            }
            if (isStudent) {
                return <Navigate to="/student/dashboard" replace/>;
            }
        }

        return <Navigate to="/info" replace/>;
    };

    // Protected Landing Page - redirects authenticated users to their dashboards
    const ProtectedLandingPage = () => {
        const userRoles = user?.roles || [];
        const isStudent = userRoles.includes("student");
        const isTeacher = userRoles.includes("teacher");
        const isAdmin = userRoles.includes("admin") || userRoles.includes("administrator");

        if (isLoading) {
            return <div>Loading...</div>;
        }

        // If user is authenticated, redirect to appropriate dashboard
        if (isAuthenticated) {
            if (isAdmin) {
                return <Navigate to="/admin/dashboard" replace/>;
            }
            if (isTeacher) {
                return <Navigate to="/teacher/dashboard" replace/>;
            }
            if (isStudent) {
                return <Navigate to="/student/dashboard" replace/>;
            }
        }

        // If not authenticated, show the landing page
        return (
            <RouteContent fullWidth={true}>
                <Login/>
            </RouteContent>
        );
    };

    // Protected route components
    const ProtectedStudentDashboard = () => {
        const userRoles = user?.roles || [];
        const isStudent = userRoles.includes("student");
        const isTeacher = userRoles.includes("teacher");

        if (isLoading) return <div>Loading...</div>;
        if (!isAuthenticated) return <Navigate to="/info" replace/>;
        if (!user) return <div>Loading user data...</div>;

        if (!isStudent) {
            if (isTeacher) return <Navigate to="/teacher/dashboard" replace/>;
            return <Navigate to="/info" replace/>;
        }

        return <StudentDashboard accessToken={accessToken} claims={user}/>;
    };

    const ProtectedTeacherDashboard = () => {
        const userRoles = user?.roles || [];
        const isStudent = userRoles.includes("student");
        const isTeacher = userRoles.includes("teacher");

        if (isLoading) return <div>Loading...</div>;
        if (!isAuthenticated) return <Navigate to="/info" replace/>;
        if (!user) return <div>Loading user data...</div>;

        if (!isTeacher) {
            if (isStudent) return <Navigate to="/student/dashboard" replace/>;
            return <Navigate to="/info" replace/>;
        }

        return <TeacherDashboard accessToken={accessToken} claims={user}/>;
    };

    const ProtectedAdminDashboard = () => {
        const userRoles = user?.roles || [];
        const isStudent = userRoles.includes("student");
        const isTeacher = userRoles.includes("teacher");
        const isAdmin = userRoles.includes("admin") || userRoles.includes("administrator");

        if (isLoading) return <div>Loading...</div>;
        if (!isAuthenticated) return <Navigate to="/info" replace/>;
        if (!user) return <div>Loading user data...</div>;

        if (!isAdmin) {
            if (isTeacher) return <Navigate to="/teacher/dashboard" replace/>;
            if (isStudent) return <Navigate to="/student/dashboard" replace/>;
            return <Navigate to="/info" replace/>;
        }

        return <AdminDashboard accessToken={accessToken} claims={user}/>;
    };

    const ProtectedAuditLogs = () => {
        const userRoles = user?.roles || [];
        const isStudent = userRoles.includes("student");
        const isTeacher = userRoles.includes("teacher");
        const isAdmin = userRoles.includes("admin") || userRoles.includes("administrator");

        if (isLoading) return <div>Loading...</div>;
        if (!isAuthenticated) return <Navigate to="/info" replace/>;
        if (!user) return <div>Loading user data...</div>;

        if (!isAdmin) {
            if (isTeacher) return <Navigate to="/teacher/dashboard" replace/>;
            if (isStudent) return <Navigate to="/student/dashboard" replace/>;
            return <Navigate to="/info" replace/>;
        }

        return <AuditAnalytics accessToken={accessToken} standalone={true}/>;
    };

    const ProtectedUserManagement = () => {
        const userRoles = user?.roles || [];
        const isStudent = userRoles.includes("student");
        const isTeacher = userRoles.includes("teacher");
        const isAdmin = userRoles.includes("admin") || userRoles.includes("administrator");

        if (isLoading) return <div>Loading...</div>;
        if (!isAuthenticated) return <Navigate to="/info" replace/>;
        if (!user) return <div>Loading user data...</div>;

        if (!isAdmin) {
            if (isTeacher) return <Navigate to="/teacher/dashboard" replace/>;
            if (isStudent) return <Navigate to="/student/dashboard" replace/>;
            return <Navigate to="/info" replace/>;
        }

        return <UserManagement accessToken={accessToken} standalone={true}/>;
    };

    const ProtectedCourseManagement = () => {
        const userRoles = user?.roles || [];
        const isStudent = userRoles.includes("student");
        const isTeacher = userRoles.includes("teacher");
        const isAdmin = userRoles.includes("admin") || userRoles.includes("administrator");

        if (isLoading) return <div>Loading...</div>;
        if (!isAuthenticated) return <Navigate to="/info" replace/>;
        if (!user) return <div>Loading user data...</div>;

        if (!isAdmin) {
            if (isTeacher) return <Navigate to="/teacher/dashboard" replace/>;
            if (isStudent) return <Navigate to="/student/dashboard" replace/>;
            return <Navigate to="/info" replace/>;
        }

        return <CourseManagement accessToken={accessToken} standalone={true}/>;
    };

    // Create a route-specific wrapper component that decides whether to use a container
    const RouteContent = ({children, fullWidth = false}) => {
        return fullWidth ? (
            <Box sx={{width: '100%', height: '100%', flex: '1 1 auto'}}>
                {children}
            </Box>
        ) : (
            <Container maxWidth="xl" sx={{mt: 2, height: '100%', flex: '1 1 auto'}}>
                {children}
            </Container>
        );
    };

    // Footer wrapper to conditionally show footer based on route
    const FooterWrapper = () => {
        const location = useLocation();
        const publicPages = ['/impressum', '/datenschutz'];
        const shouldShowFooter = isAuthenticated || publicPages.includes(location.pathname);

        return shouldShowFooter ? <Footer/> : null;
    };

    return (
        <TokenProvider refreshFunction={refreshAccessToken} getAccessTokenFunction={getAccessToken}>
            <ThemeProvider theme={theme}>
                <CssBaseline/>
                <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '100vh',
                    height: '100vh',
                    overflow: 'hidden' // Prevent body scrolling
                }}
            >
                <BrowserRouter>
                    {isAuthenticated && (
                        <AppBar darkMode={darkMode} setDarkMode={setDarkMode} claims={user} accessToken={accessToken}/>
                    )}
                    <Box
                        sx={{
                            display: 'flex',
                            flexGrow: 1,
                            width: '100%',
                            // Conditionally apply height and margin based on authentication
                            ...(isAuthenticated ? {
                                height: 'calc(100vh - 64px)', // Viewport height minus navbar height
                                mt: '64px', // Add top margin to account for fixed navbar
                            } : {
                                height: '100vh', // Full height when no navbar
                            }),
                        }}
                    >
                        {/* Sidebar - Only visible when authenticated */}
                        {isAuthenticated && (
                            // placeholder for Sidebar component
                            <></>
                        )}

                        {/* Main content area */}
                        <Box
                            component="main"
                            sx={{
                                flexGrow: 1,
                                boxSizing: 'border-box',
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'auto', // Enable scrolling for this container
                            }}
                        >
                            <Routes>
                                <Route path="/" element={<RootRedirect claims={user}/>}/>

                                {/* Protected landing page */}
                                <Route path="/info" element={<ProtectedLandingPage/>}/>

                                {/* Public pages */}
                                <Route path="/impressum" element={<Impressum/>}/>
                                <Route path="/datenschutz" element={<Datenschutz/>}/>

                                <Route path="/callback" element={
                                    <RouteContent>
                                        <Callback/>
                                    </RouteContent>
                                }/>

                                {/* Protected routes */}
                                <Route element={<RequireAuth/>}>
                                    {/* Student routes */}
                                    <Route path="/student/dashboard" element={<ProtectedStudentDashboard/>}/>
                                    <Route path="/student/course/:courseId"
                                           element={<StudentCoursePage accessToken={accessToken} claims={user}/>}/>
                                    <Route path="/student/course/:courseId/topic/:topicId"
                                           element={<StudentTopicPage accessToken={accessToken} claims={user}/>}/>
                                    <Route path="/student/progress"
                                           element={<StudentGamification accessToken={accessToken}
                                                                         userId={user?.sub}/>}/>
                                    <Route path="/student/minigame"
                                           element={<MinigamePage accessToken={accessToken} claims={user}/>}/>

                                    {/* Teacher routes */}
                                    <Route path="/teacher/dashboard" element={<ProtectedTeacherDashboard/>}/>
                                    <Route path="/teacher/analytics"
                                           element={<Analytics accessToken={accessToken} claims={user}/>}/>
                                    <Route path="/teacher/analytics/course/:courseId"
                                           element={<Analytics accessToken={accessToken} claims={user}
                                                               courseSpecific={true}/>}/>
                                    <Route path="/teacher/course/:courseId"
                                           element={<TeacherCourseDashboard accessToken={accessToken} claims={user}/>}/>
                                    <Route path="/teacher/course/:courseId/topic/:topicId"
                                           element={<TeacherTopicDashboard accessToken={accessToken} claims={user}/>}/>

                                    {/* Admin routes */}
                                    <Route path="/admin/dashboard" element={<ProtectedAdminDashboard/>}/>
                                    <Route path="/admin/audit-logs" element={<ProtectedAuditLogs/>}/>
                                    <Route path="/admin/user-management" element={<ProtectedUserManagement/>}/>
                                    <Route path="/admin/course-management" element={<ProtectedCourseManagement/>}/>
                                </Route>
                                <Route path="*" element={<Navigate to="/" replace/>}/>
                            </Routes>
                        </Box>

                    </Box>
                    <FooterWrapper/>
                </BrowserRouter>
            </Box>
        </ThemeProvider>
        </TokenProvider>
    );
};

export default App;